import { ActivityIndicator, Alert, Platform } from 'react-native';
import { Select } from '../../../components/primitives';
import { useTranslation } from '../../../contexts/TranslationContext';
import { useSellerProfiles } from '../../../hooks/useSellerProfiles';
import type { SellerStatus } from '../../../types/leads';
import { SELLER_STATUS_LABELS, SELLER_TYPE_LABELS } from '../../../types/leads';
import { useMembers } from '../../settings/hooks/useSettings';

interface AssigneePickerProps {
  organizationId: string;
  value: string | null;
  onChange: (userId: string | null) => void;
  disabled?: boolean;
}

const UNASSIGNED_VALUE = '__unassigned__';

export function AssigneePicker({
  organizationId,
  value,
  onChange,
  disabled,
}: Readonly<AssigneePickerProps>) {
  const { t } = useTranslation();
  const { data: membersResponse, isLoading: membersLoading } = useMembers(organizationId);
  const { data: sellerProfiles, isLoading: profilesLoading } = useSellerProfiles(organizationId);

  const isLoading = membersLoading || profilesLoading;

  if (isLoading) {
    return <ActivityIndicator size="small" />;
  }

  const members = membersResponse?.data ?? [];
  const profileByUserId = new Map(
    (sellerProfiles ?? []).map((profile) => [profile.userId, profile]),
  );

  const activeOptions = members
    .filter((member) => {
      const profile = profileByUserId.get(member.userId);
      return !profile || profile.status === 'active';
    })
    .map((member) => {
      const profile = profileByUserId.get(member.userId);
      const typeLabel = profile ? SELLER_TYPE_LABELS[profile.sellerType] : undefined;
      const capacityInfo = profile?.maxLeads
        ? ` (${profile.currentLeadCount ?? 0}/${profile.maxLeads})`
        : '';
      const label = typeLabel
        ? `${member.fullName} · ${typeLabel}${capacityInfo}`
        : member.fullName;
      return { value: member.userId, label };
    });

  const onLeaveOptions = members
    .filter((member) => {
      const profile = profileByUserId.get(member.userId);
      return profile?.status === 'on_leave';
    })
    .map((member) => {
      const profile = profileByUserId.get(member.userId)!;
      const typeLabel = SELLER_TYPE_LABELS[profile.sellerType];
      const statusLabel = SELLER_STATUS_LABELS[profile.status as SellerStatus];
      const label = `${member.fullName} · ${typeLabel} (${statusLabel})`;
      return { value: member.userId, label };
    });

  const options = [
    { value: UNASSIGNED_VALUE, label: t('leads.assigneePicker.unassigned') },
    ...activeOptions,
    ...onLeaveOptions,
  ];

  const selectValue = value ?? UNASSIGNED_VALUE;

  const confirmOverCapacity = (
    userId: string,
    profile: { maxLeads: number | null; currentLeadCount?: number | null },
    memberName: string,
  ) => {
    if (profile.maxLeads == null) {
      onChange(userId);
      return;
    }
    const current = profile.currentLeadCount ?? 0;
    const message = t('leads.assigneePicker.overCapacityWarning', {
      name: memberName,
      current,
      max: profile.maxLeads,
    });

    if (Platform.OS === 'web') {
      if (globalThis.confirm(message)) {
        onChange(userId);
      }
    } else {
      Alert.alert(t('leads.assigneePicker.overCapacityTitle'), message, [
        { text: t('common.actions.cancel'), style: 'cancel' },
        { text: t('leads.assigneePicker.assignAnyway'), onPress: () => onChange(userId) },
      ]);
    }
  };

  const handleChange = (selected: string) => {
    if (selected === UNASSIGNED_VALUE) {
      onChange(null);
      return;
    }

    const profile = profileByUserId.get(selected);
    if (profile?.maxLeads) {
      const current = profile.currentLeadCount ?? 0;
      if (current >= profile.maxLeads) {
        const member = members.find((m) => m.userId === selected);
        confirmOverCapacity(selected, profile, member?.fullName ?? 'Unknown');
        return;
      }
    }

    onChange(selected);
  };

  return (
    <Select
      variant="dropdown"
      options={options}
      value={selectValue}
      onChange={handleChange}
      placeholder={t('leads.assigneePicker.placeholder')}
      testID="assignee_picker"
      searchable
      searchPlaceholder={t('common.selectSearchPlaceholder')}
      emptySearchLabel={t('common.selectNoMatches')}
    />
  );
}
